// scoreService - API client for score operations
// Implements US-3.2: RICE Scoring Interface and Calculation

import apiClient from '../utils/apiClient';
import { getToken } from '../utils/tokenStorage';

/**
 * SCORE update request structure
 * NOTE: Sends array of criterion-value pairs to backend
 */
export interface ScoreUpdateRequest {
  scores: {
    criterion: string;
    value: number | string;
  }[];
}

/**
 * SCORE update response structure
 * NOTE: Returns updated scores and calculated result
 */
export interface ScoreUpdateResponse {
  success: boolean;
  scores?: any[];
  calculatedResult?: any;
  error?: string;
}

/**
 * UPDATE scores for an item
 * NOTE: Backend calculates result automatically
 */
export const updateScores = async (
  itemId: string,
  scores: ScoreUpdateRequest['scores']
): Promise<ScoreUpdateResponse> => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await apiClient.put(
      `/api/items/${itemId}/scores`,
      { scores },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return {
      success: true,
      scores: response.data.scores,
      calculatedResult: response.data.calculatedResult,
    };
  } catch (error: any) {
    console.error('Update scores error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'An unexpected error occurred',
    };
  }
};

/**
 * GET scores for an item
 * NOTE: Returns all criterion scores for the item
 */
export const getItemScores = async (itemId: string): Promise<any> => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await apiClient.get(`/api/items/${itemId}/scores`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      success: true,
      scores: response.data.scores,
    };
  } catch (error: any) {
    console.error('Get scores error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'An unexpected error occurred',
    };
  }
};
